import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  CircularProgress,
  Button,
  Grid,
  Typography,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";
import useAxiosPrivate from "../../common/useAxiosPrivate";
import { toast } from "react-toastify";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import OfflinePinIcon from "@mui/icons-material/OfflinePin";
import { responsibleOptions } from "./responsibleOptions";
import { useSelector } from "react-redux";
import { IUserInfo } from "../../store/actions";

export interface ReworkProps {
  chassisNo: string;
  model: string;
  reworkType: string;
  checkedBy: string;
}

interface ReworkItem {
  itemNo: number;
  problemDescription: string;
  comment: string;
  whoFoundFault: string;
  responsible: string;
  whoDidRework: string;
  timeSpentOnRework: {
    hours: string;
    minutes: string;
    seconds: string;
  };
  completed: boolean;
}

const NOTE_ONLY_REWORK_TYPES = new Set([
  "gp-quality-inspection",
  // "final-electrical-inspection",
  "final-audit",
  "svi-checklist",
  "qc-svi-checklist",
]);

const toNoteOnlyDescription = (problemDescription: string, reworkType: string) => {
  if (!NOTE_ONLY_REWORK_TYPES.has(reworkType)) {
    return problemDescription;
  }

  const trimmed = (problemDescription || "").trim();
  if (!trimmed) {
    return trimmed;
  }

  const markerIndex = trimmed.lastIndexOf(":");
  if (markerIndex === -1) {
    return trimmed;
  }

  const note = trimmed.slice(markerIndex + 1).trim();
  return note || trimmed;
};

const Rework: React.FC<ReworkProps> = ({
  chassisNo,
  model,
  reworkType,
  checkedBy,
}) => {
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState(false);
  const [reworkItems, setReworkItems] = useState<ReworkItem[]>([]);

  const authInfo: IUserInfo = useSelector(
    (state: any) => state.userAuthInfoState
  );
  const userRole = authInfo.roles;
  const isElectricalQC = userRole?.includes("electrical-qc");

  const getReworkStatus = () => {
    if (reworkItems.length === 0) return null;
    return reworkItems.every((item) => item.completed)
      ? "COMPLETE"
      : "INCOMPLETE";
  };

  const reworkStatus = getReworkStatus();

  useEffect(() => {
    const fetchReworkData = async () => {
      setLoading(true);
      try {
        const endpoint =
          reworkType === "qc_electrical_chassis_prep"
            ? `prepare_electrical_chassis_prep_rework/${chassisNo}`
            : `prepare_rework/${chassisNo}`;

        const response = await axiosPrivate.post(endpoint, null, {
          params: { rework_type: reworkType, station_name: reworkType },
        });

        const fetchedItems = response.data?.reworkItems || [];

        const mappedItems = fetchedItems.map((item: ReworkItem) => ({
          ...item,
          problemDescription: toNoteOnlyDescription(
            item.problemDescription,
            reworkType
          ),
          whoFoundFault: item.whoFoundFault || checkedBy || "",
          timeSpentOnRework: {
            hours:
              item.timeSpentOnRework?.hours?.toString().padStart(2, "0") ||
              "00",
            minutes:
              item.timeSpentOnRework?.minutes?.toString().padStart(2, "0") ||
              "00",
            seconds:
              item.timeSpentOnRework?.seconds?.toString().padStart(2, "0") ||
              "00",
          },
        }));

        setReworkItems(mappedItems);
      } catch (error) {
        console.error("Error fetching rework data:", error);
        toast.error("Failed to fetch rework data.");
      } finally {
        setLoading(false);
      }
    };

    if (chassisNo) fetchReworkData();
  }, [chassisNo, reworkType, axiosPrivate, checkedBy]);

  const handleFieldChange = (
    index: number,
    field: keyof ReworkItem,
    value: string | boolean
  ) => {
    setReworkItems((prev) =>
      prev.map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item
      )
    );
  };

  const handleTimeChange = (
    index: number,
    timeField: "hours" | "minutes",
    value: string
  ) => {
    const numericOnly = value.replace(/[^0-9]/g, "").slice(0, 2);
    setReworkItems((prev) =>
      prev.map((item, idx) =>
        idx === index
          ? {
              ...item,
              timeSpentOnRework: {
                ...item.timeSpentOnRework,
                [timeField]: numericOnly,
              },
            }
          : item
      )
    );
  };

  const saveData = async () => {
    if (reworkItems.length === 0) {
      toast.error("Please add at least one row to save.");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        chassisNo,
        station_name: reworkType,
        model,
        reworkItems: reworkItems.map((item) => ({
          ...item,
          timeSpentOnRework: {
            hours: parseInt(item.timeSpentOnRework.hours || "0", 10),
            minutes: parseInt(item.timeSpentOnRework.minutes || "0", 10),
            seconds: parseInt(item.timeSpentOnRework.seconds || "0", 10),
          },
        })),
      };

      await axiosPrivate.post(`rework/save`, payload, {
        params: { rework_type: reworkType },
      });

      toast.success("Rework data saved successfully!");
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Failed to save rework data.");
    } finally {
      setLoading(false);
    }
  };

  const addNewRow = () => {
    setReworkItems((prev) => [
      ...prev,
      {
        itemNo: prev.length + 1,
        problemDescription: "",
        comment: "",
        whoFoundFault: checkedBy,
        responsible: "",
        whoDidRework: "",
        timeSpentOnRework: { hours: "00", minutes: "00", seconds: "00" },
        completed: false,
      },
    ]);
  };

  return (
    <div>
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          {reworkItems.length > 0 && (
            <Typography
              sx={{
                mb: 2,
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "5px",
                color:
                  reworkStatus === "INCOMPLETE" ? "error.main" : "success.main",
              }}
            >
              {reworkStatus === "INCOMPLETE" ? (
                <>⚠️ INCOMPLETE</>
              ) : (
                <>
                  <OfflinePinIcon fontSize="medium" /> COMPLETE
                </>
              )}
            </Typography>
          )}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow className="table__head_row">
                  <TableCell
                    className="table__head_cell"
                    rowSpan={2}
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid white",
                      width: "25%",
                    }}
                  >
                    <Typography sx={{ fontSize: "16px", fontWeight: "bold" }}>
                      Problem / Description
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontStyle: "italic",
                        textDecoration: "underline",
                      }}
                    >
                      ensure items detailed with fault type & location
                    </Typography>
                  </TableCell>
                  <TableCell
                    className="table__head_cell"
                    rowSpan={2}
                    align="center"
                    sx={{
                      "& .MuiInputBase-input": { padding: 0 },
                      "& .MuiInputBase-root": {
                        border: "none",
                        backgroundColor: "transparent",
                      },
                      width: "25%",
                    }}
                  >
                    Comment
                  </TableCell>
                  <TableCell
                    className="table__head_cell"
                    rowSpan={2}
                    align="center"
                    sx={{
                      "& .MuiInputBase-input": { padding: 0 },
                      "& .MuiInputBase-root": {
                        border: "none",
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    Who Found Fault
                  </TableCell>
                  <TableCell
                    className="table__head_cell"
                    rowSpan={2}
                    align="center"
                    sx={{
                      "& .MuiInputBase-input": { padding: 0 },
                      "& .MuiInputBase-root": {
                        border: "none",
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    Responsible
                  </TableCell>
                  <TableCell
                    className="table__head_cell"
                    colSpan={3}
                    align="center"
                    sx={{
                      "& .MuiInputBase-input": { padding: 0 },
                      "& .MuiInputBase-root": {
                        border: "none",
                        backgroundColor: "transparent",
                      },
                      borderRight: "1px solid #d0d0d0",
                    }}
                  >
                    Completed
                  </TableCell>
                </TableRow>

                <TableRow className="table__head_row">
                  <TableCell
                    className="table__head_cell"
                    align="center"
                    sx={{
                      "& .MuiInputBase-input": { padding: 0 },
                      "& .MuiInputBase-root": {
                        border: "none",
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    Who Did Rework
                  </TableCell>
                  <TableCell
                    className="table__head_cell"
                    align="center"
                    sx={{
                      "& .MuiInputBase-input": { padding: 0 },
                      "& .MuiInputBase-root": {
                        border: "none",
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    ✔
                  </TableCell>
                  <TableCell
                    className="table__head_cell"
                    align="center"
                    sx={{
                      "& .MuiInputBase-input": { padding: 0 },
                      "& .MuiInputBase-root": {
                        border: "none",
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    Time Spent on Rework
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reworkItems.map((item, index) => (
                  <TableRow key={item.itemNo}>
                    <TableCell sx={{ borderLeft: "1px solid #d0d0d0" }}>
                      <TextField
                        disabled={isElectricalQC}
                        variant="standard"
                        value={item.problemDescription}
                        onChange={(e) =>
                          handleFieldChange(
                            index,
                            "problemDescription",
                            e.target.value
                          )
                        }
                        fullWidth
                        multiline
                        InputProps={{
                          disableUnderline: true,
                          style: { fontSize: "16px" },
                        }}
                        sx={{
                          "& .MuiInputBase-input": { padding: 0 },
                          "& .MuiInputBase-root": {
                            border: "none",
                            backgroundColor: "transparent",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ borderLeft: "1px solid #d0d0d0" }}>
                      <TextField
                        disabled={isElectricalQC}
                        variant="standard"
                        value={item.comment}
                        onChange={(e) =>
                          handleFieldChange(index, "comment", e.target.value)
                        }
                        fullWidth
                        multiline
                        InputProps={{
                          disableUnderline: true,
                          style: { fontSize: "16px" },
                        }}
                        sx={{
                          "& .MuiInputBase-input": { padding: 0 },
                          "& .MuiInputBase-root": {
                            border: "none",
                            backgroundColor: "transparent",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ borderLeft: "1px solid #d0d0d0" }}>
                      <TextField
                        disabled={isElectricalQC}
                        variant="standard"
                        value={item.whoFoundFault}
                        onChange={(e) =>
                          handleFieldChange(
                            index,
                            "whoFoundFault",
                            e.target.value
                          )
                        }
                        fullWidth
                        InputProps={{
                          disableUnderline: true,
                          style: { fontSize: "16px" },
                        }}
                        sx={{
                          "& .MuiInputBase-input": { padding: 0 },
                          "& .MuiInputBase-root": {
                            border: "none",
                            backgroundColor: "transparent",
                          },
                        }}
                      />
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{ borderLeft: "1px solid #d0d0d0" }}
                    >
                      <Select
                        value={item.responsible || ""}
                        disabled={isElectricalQC}
                        onChange={(e) =>
                          handleFieldChange(
                            index,
                            "responsible",
                            e.target.value
                          )
                        }
                        variant="standard"
                        fullWidth
                        displayEmpty
                        sx={{
                          "& .MuiSelect-select": {
                            padding: "8px",
                            fontSize: "16px",
                          },
                          "&:before, &:after": {
                            borderBottom: "none !important",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          backgroundColor: "transparent",
                        }}
                      >
                        {responsibleOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>

                    <TableCell sx={{ borderLeft: "1px solid #d0d0d0" }}>
                      <TextField
                        disabled={isElectricalQC}
                        variant="standard"
                        value={item.whoDidRework}
                        onChange={(e) =>
                          handleFieldChange(
                            index,
                            "whoDidRework",
                            e.target.value
                          )
                        }
                        fullWidth
                        InputProps={{
                          disableUnderline: true,
                          style: { fontSize: "16px" },
                        }}
                        sx={{
                          "& .MuiInputBase-input": { padding: 0 },
                          "& .MuiInputBase-root": {
                            border: "none",
                            backgroundColor: "transparent",
                          },
                        }}
                      />
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{ borderLeft: "1px solid #d0d0d0" }}
                    >
                      {!isElectricalQC ? (
                        <Tooltip
                          title={
                            !item.whoDidRework || !item.responsible
                              ? "'Who Did Rework' and 'Responsible' must be filled to enable the Checkbox."
                              : ""
                          }
                          arrow
                          disableHoverListener={
                            !!item.whoDidRework && !!item.responsible
                          }
                        >
                          <span>
                            <Checkbox
                              checked={item.completed}
                              disabled={
                                !item.whoDidRework ||
                                !item.responsible ||
                                isElectricalQC
                              }
                              onChange={(e) =>
                                handleFieldChange(
                                  index,
                                  "completed",
                                  e.target.checked
                                )
                              }
                              sx={{
                                opacity:
                                  !item.whoDidRework || !item.responsible
                                    ? 0.5
                                    : 1,
                                filter:
                                  !item.whoDidRework || !item.responsible
                                    ? "blur(1px)"
                                    : "none",
                              }}
                            />
                          </span>
                        </Tooltip>
                      ) : (
                        <Checkbox
                          checked={item.completed}
                          disabled={
                            !item.whoDidRework ||
                            !item.responsible ||
                            isElectricalQC
                          }
                          onChange={(e) =>
                            handleFieldChange(
                              index,
                              "completed",
                              e.target.checked
                            )
                          }
                          sx={{
                            opacity:
                              !item.whoDidRework || !item.responsible ? 0.5 : 1,
                            filter:
                              !item.whoDidRework || !item.responsible
                                ? "blur(1px)"
                                : "none",
                          }}
                        />
                      )}
                    </TableCell>

                    <TableCell
                      sx={{
                        justifyContent: "center",
                        alignItems: "center",
                        borderLeft: "1px solid #d0d0d0",
                        textAlign: "center",
                        borderRight: "1px solid #d0d0d0",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <TextField
                          disabled={isElectricalQC}
                          variant="standard"
                          placeholder="HH"
                          value={item.timeSpentOnRework.hours || ""}
                          onChange={(e) =>
                            handleTimeChange(index, "hours", e.target.value)
                          }
                          size="small"
                          sx={{
                            width: "2em",
                            textAlign: "center",
                            fontSize: "16px",
                          }}
                        />

                        <TextField
                          disabled={isElectricalQC}
                          variant="standard"
                          placeholder="MM"
                          value={item.timeSpentOnRework.minutes || ""}
                          onChange={(e) =>
                            handleTimeChange(index, "minutes", e.target.value)
                          }
                          size="small"
                          sx={{
                            width: "2em",
                            textAlign: "center",
                            fontSize: "16px",
                          }}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {!isElectricalQC && (
            <Grid container spacing={2} justifyContent="center" mt={2}>
              <Grid item>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<NoteAddIcon />}
                  onClick={addNewRow}
                >
                  Add Row
                </Button>
              </Grid>
              <Grid item>
                <Button variant="contained" color="primary" onClick={saveData}>
                  Save
                </Button>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </div>
  );
};

export default Rework;
